import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import AppHeader from '@/components/AppHeader';
import { Tournament, Team } from '@/types';
import { getTournaments, deleteTournament, getTournamentStats, getTeams } from '@/lib/supabase';
import TournamentForm from '@/components/modals/TournamentForm';

interface TournamentWithStats extends Tournament {
  stats?: {
    total: number;
    completed: number;
    live: number;
    pending: number;
  };
}

interface TournamentManagementProps {
  onSignOut: () => void;
}

export default function TournamentManagement({ onSignOut }: TournamentManagementProps) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [tournaments, setTournaments] = useState<TournamentWithStats[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const formatTeams = (data: Array<{ id: string; name: string; short_name: string; color: string; logo_url?: string | null; players: Array<{ id: string; name: string; number: number; team_id: string }> }>): Team[] =>
    data.map(t => ({
      id: t.id,
      name: t.name,
      shortName: t.short_name,
      color: t.color,
      logoUrl: t.logo_url,
      players: t.players.map(p => ({
        id: p.id,
        name: p.name,
        number: p.number,
        teamId: p.team_id,
      })),
    }));

  const loadTournaments = async () => {
    setLoading(true);
    try {
      const data = await getTournaments();
      const tournamentsWithStats = await Promise.all(
        data.map(async (t) => {
          try {
            const stats = await getTournamentStats(t.id);
            return { ...t, stats };
          } catch {
            return { ...t, stats: { total: 0, completed: 0, live: 0, pending: 0, scheduled: 0 } };
          }
        })
      );
      setTournaments(tournamentsWithStats);
    } catch (err) {
      console.error('Failed to load tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await loadTournaments();
      const teamsData = await getTeams();
      setTeams(formatTeams(teamsData));
    };
    loadData();
  }, []);

  const handleCreate = () => {
    setEditingTournament(null);
    setShowForm(true);
  };

  const handleEdit = (tournament: Tournament) => {
    setEditingTournament(tournament);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tournament? Matches will be unlinked but preserved.')) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteTournament(id);
      await loadTournaments();
    } catch (err) {
      console.error('Failed to delete tournament:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleFormSuccess = async () => {
    setShowForm(false);
    setEditingTournament(null);
    await loadTournaments();
  };

  const handleViewTournament = (tournament: Tournament) => {
    navigate(`/tournament/${tournament.id}`);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <AppHeader
        onSignOut={onSignOut}
        title="Tournaments"
        rightExtra={
          !!isAdmin && (
            <button
              type="button"
              onClick={handleCreate}
              className="p-2 rounded-lg text-success hover:text-success/80 hover:bg-surface-raised transition-colors"
              aria-label="New tournament"
              title="New Tournament"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 5v10M5 10h10" />
              </svg>
            </button>
          )
        }
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 safe-area-bottom">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tournaments.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-surface-raised flex items-center justify-center mx-auto mb-4">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-muted">
                <path d="M16 8v16M8 16h16" />
              </svg>
            </div>
            <p className="text-text-secondary mb-4">No tournaments yet</p>
            {!!isAdmin && (
              <button onClick={handleCreate} className="btn-primary bg-success text-white">
                Create Tournament
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3 max-w-md mx-auto">
            {tournaments.map((tournament) => (
              <div
                key={tournament.id}
                className="card"
              >
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => handleViewTournament(tournament)}
                >
                  <div className="flex-1">
                    <h3 className="font-bold text-text-primary">{tournament.name}</h3>
                    <p className="text-sm text-text-secondary">
                      {new Date(tournament.date).toLocaleDateString()}
                      {tournament.venue && ` - ${tournament.venue}`}
                    </p>
                    {tournament.stats && (
                      <div className="flex gap-3 mt-2 text-xs">
                        <span className="text-text-muted">
                          {tournament.stats.total} matches
                        </span>
                        <span className="text-success">
                          {tournament.stats.completed} done
                        </span>
                        {tournament.stats.live > 0 && (
                          <span className="text-warning">
                            {tournament.stats.live} live
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!!isAdmin && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(tournament);
                        }}
                        className="p-2 text-text-muted hover:text-text-primary transition-colors"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    )}
                    {!!isAdmin && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(tournament.id);
                        }}
                        disabled={deletingId === tournament.id}
                        className="p-2 text-text-muted hover:text-error transition-colors disabled:opacity-50"
                      >
                        {deletingId === tournament.id ? (
                          <span className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin inline-block" />
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        )}
                      </button>
                    )}
                    <span className="text-text-muted">{'>'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tournament Form Modal */}
      {showForm && (
        <TournamentForm
          tournament={editingTournament}
          teams={teams}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false);
            setEditingTournament(null);
          }}
        />
      )}
    </div>
  );
}
